import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useToast } from 'contexts/ToastContext';
import { theme } from 'styles/theme';

/**
 * Confirm 다이얼로그 — ToastContext confirmDialog API 유지, 색은 theme 토큰
 */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

function typeColor(type) {
  switch (type) {
    case 'success':
      return theme.colors.status.success;
    case 'error':
      return theme.colors.status.error;
    case 'warning':
      return theme.colors.status.warning;
    case 'info':
    default:
      return theme.colors.blue;
  }
}

const DialogContainer = styled.div`
  min-width: 400px;
  max-width: 500px;
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.md};
  border: 3px solid ${(p) => typeColor(p.$dialogType)};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const DialogContent = styled.div`
  padding: ${theme.spacing.lg};
`;

const DialogMessage = styled.div`
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.lg};
  font-weight: 500;
  margin-bottom: ${theme.spacing.lg};
  line-height: 1.5;
  white-space: pre-line;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--erp-form-row-gap, ${theme.components.form.rowGap});
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ConfirmButton = styled(Button)`
  background-color: ${(p) => typeColor(p.$dialogType)};
  color: ${theme.colors.white};

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const CancelButton = styled(Button)`
  background-color: ${theme.colors.background.medium};
  color: ${theme.colors.text.primary};

  &:hover {
    background-color: ${theme.colors.border};
  }
`;

const ConfirmDialog = () => {
  const { confirmDialog, handleConfirm, handleCancel } = useToast();

  // 스페이스/Enter로 확인 — 열자마자 같은 키다운이 확인으로 새는 것 방지
  useEffect(() => {
    if (!confirmDialog) {
      return undefined;
    }

    let armed = false;
    const armTimer = window.setTimeout(() => {
      armed = true;
    }, 50);

    const handleKeyDown = (e) => {
      if (!armed) {
        if (/^F([1-9]|1[0-2])$/i.test(e.key || '')) {
          e.preventDefault();
          e.stopPropagation();
        }
        return;
      }
      if (e.key === ' ' || e.key === 'Spacebar' || e.key === 'Enter') {
        e.preventDefault();
        handleConfirm();
      }
      if (e.key === 'Escape') {
        handleCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.clearTimeout(armTimer);
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [confirmDialog, handleConfirm, handleCancel]);

  if (!confirmDialog) {
    return null;
  }

  return (
    <Overlay>
      <DialogContainer $dialogType={confirmDialog.type}>
        <DialogContent>
          <DialogMessage>{confirmDialog.message}</DialogMessage>
          <ButtonGroup>
            <ConfirmButton $dialogType={confirmDialog.type} onClick={handleConfirm}>
              {confirmDialog.confirmText}
            </ConfirmButton>
            <CancelButton onClick={handleCancel}>{confirmDialog.cancelText}</CancelButton>
          </ButtonGroup>
        </DialogContent>
      </DialogContainer>
    </Overlay>
  );
};

export default ConfirmDialog;
